import React, { useState } from 'react';
import { 
  Search, X, Send, User, ArrowLeft, MessageSquare, 
  Trash2, LifeBuoy, CheckCircle2, Clock, AlertCircle, ChevronRight, CornerDownLeft, 
  ArrowRight
} from 'lucide-react';

// --- Dummy Data ---
const initialIssues = [
  { 
    id: 'TKT-001', raisedBy: 'Rahul Sharma', raisedByType: 'BUYER', target: 'Electric Supplier', 
    category: 'Payment Issue', description: 'Payment deducted but order not confirmed.', 
    dateTime: '2026-01-29 10:30 AM', status: 'PENDING',
    history: [
      { sender: 'Rahul Sharma', role: 'BUYER', message: 'I paid ₹5,000 via UPI, but it says payment failed on the site.', time: '10:30 AM' },
      { sender: 'System', role: 'SYSTEM', message: 'Ticket assigned to Senior Support.', time: '10:35 AM' }
    ]
  },
  { 
    id: 'TKT-002', raisedBy: 'Home Supplier', raisedByType: 'SELLER', target: 'Aman Varma', 
    category: 'Returns', description: 'Customer returned damaged product.', 
    dateTime: '2026-01-28 02:15 PM', status: 'PENDING',
    history: [
      { sender: 'Home Supplier', role: 'SELLER', message: 'The washing machine was returned with a broken tub.', time: '02:15 PM' }
    ]
  },
  { 
    id: 'TKT-003', raisedBy: 'Tech World', raisedByType: 'SELLER', target: 'System', 
    category: 'Listing Error', description: 'Cannot update product stock.', 
    dateTime: '2026-01-27 09:00 AM', status: 'SOLVED',
    history: [
      { sender: 'Tech World', role: 'SELLER', message: 'Error 500 when saving.', time: '09:00 AM' },
      { sender: 'Admin', role: 'ADMIN', message: 'Fixed. Please try again.', time: '11:00 AM' }
    ]
  }
];

const IssuesPage: React.FC = () => {
  const [issues, setIssues] = useState(initialIssues);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);
  const [adminReply, setAdminReply] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSolve = (id: string) => {
    setIssues(prev => prev.map(i => i.id === id ? { ...i, status: 'SOLVED' } : i));
    if (selectedIssue?.id === id) setSelectedIssue({ ...selectedIssue, status: 'SOLVED' });
  };

  const addReply = () => {
    if (!adminReply || !selectedIssue) return;
    const newMessage = { sender: 'Super Admin', role: 'ADMIN', message: adminReply, time: 'Now' };
    
    const updatedIssue = { 
      ...selectedIssue, 
      history: [...selectedIssue.history, newMessage] 
    };

    setIssues(prev => prev.map(i => i.id === selectedIssue.id ? updatedIssue : i));
    setSelectedIssue(updatedIssue);
    setAdminReply('');
  };

  const filteredIssues = issues.filter(i => 
    i.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
    i.raisedBy.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- VIEW 1: TICKET LIST (Main Dashboard) ---
  if (!selectedIssue) {
    return (
      <div className="min-h-screen bg-white font-sans animate-fade-in-up">
        <div className="max-w-[1600px] mx-auto px-6 py-12">
          
          {/* Header */}
          <div className="mb-12 border-b border-gray-100 pb-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="flex items-center gap-5">
               <div className="w-14 h-14 bg-[#006d5b]/10 rounded-2xl flex items-center justify-center text-[#006d5b] shadow-sm">
                  <LifeBuoy size={30} />
               </div>
               <div>
                 <h2 className="text-3xl font-black text-gray-900 tracking-tight">Support Desk</h2>
                 <p className="text-gray-500 font-medium mt-1 text-sm">Resolve disputes and answer user queries.</p>
               </div>
            </div>

            {/* Search Bar */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                placeholder="Search Ticket ID or Name..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm font-bold focus:border-[#006d5b] focus:bg-white outline-none transition-all placeholder:text-gray-400" 
              />
            </div>
          </div>

          {/* Table View (Desktop) */}
          <div className="hidden md:block bg-white border border-gray-200 rounded-[30px] overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50/50 border-b border-gray-100 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                <tr>
                  <th className="px-8 py-5">Ticket</th>
                  <th className="px-6 py-5">Raised By</th>
                  <th className="px-6 py-5">Against</th>
                  <th className="px-6 py-5">Category</th>
                  <th className="px-6 py-5">Status</th>
                  <th className="px-8 py-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm font-medium divide-y divide-gray-50">
                {filteredIssues.map((issue) => (
                  <tr key={issue.id} onClick={() => setSelectedIssue(issue)} className="group hover:bg-gray-50/50 transition-all cursor-pointer">
                    <td className="px-8 py-5">
                      <span className="font-bold text-gray-900 group-hover:text-[#006d5b] transition-colors">{issue.id}</span>
                      <div className="text-[10px] text-gray-400 font-bold mt-0.5">{issue.dateTime}</div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                         <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-xs uppercase">
                            {issue.raisedBy.charAt(0)}
                         </div>
                         <div>
                            <div className="text-gray-900 font-bold text-sm">{issue.raisedBy}</div>
                            <div className="text-[10px] text-[#006d5b] font-black uppercase tracking-wider">{issue.raisedByType}</div>
                         </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-gray-600 font-bold text-xs uppercase">{issue.target}</td>
                    <td className="px-6 py-5">
                       <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-xs font-bold">{issue.category}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center gap-1 w-fit ${issue.status === 'SOLVED' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-orange-50 text-orange-600 border border-orange-100'}`}>
                        {issue.status === 'SOLVED' ? <CheckCircle2 size={12}/> : <Clock size={12}/>}
                        {issue.status}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="text-gray-400 group-hover:text-[#006d5b] inline-flex p-2 rounded-lg transition-all">
                        <ChevronRight size={20} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredIssues.length === 0 && (
               <div className="p-12 text-center text-gray-400 text-sm font-bold">No tickets found matching your search.</div>
            )}
          </div>

          {/* Card View (Mobile) */}
          <div className="md:hidden grid grid-cols-1 gap-5">
             {filteredIssues.map((issue) => (
                <div key={issue.id} onClick={() => setSelectedIssue(issue)} className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:border-[#006d5b] transition-all cursor-pointer relative overflow-hidden">
                   <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 bg-[#006d5b]/10 text-[#006d5b] rounded-full flex items-center justify-center font-bold">
                            {issue.raisedBy.charAt(0)}
                         </div>
                         <div>
                            <h3 className="font-black text-gray-900 text-sm">{issue.raisedBy}</h3>
                            <p className="text-[10px] text-gray-400 font-bold">{issue.id} • {issue.dateTime}</p>
                         </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-[10px] font-black uppercase ${issue.status === 'SOLVED' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'}`}>
                         {issue.status}
                      </span>
                   </div>
                   <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                      <span className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-600 font-bold">{issue.category}</span>
                      <div className="text-[#006d5b] text-xs font-black flex items-center gap-1">
                         Resolve <ArrowRight size={14} />
                      </div>
                   </div>
                </div>
             ))}
          </div>

        </div>
      </div>
    );
  }

  // --- VIEW 2: TICKET DETAIL (Full Page) ---
  return (
    <div className="min-h-screen bg-white font-sans animate-fade-in-up">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        
        {/* Detail Header */}
        <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-6">
           <button 
             onClick={() => setSelectedIssue(null)} 
             className="flex items-center gap-2 text-gray-500 hover:text-[#006d5b] font-bold text-sm bg-gray-50 hover:bg-[#006d5b]/10 px-4 py-2 rounded-xl transition-all"
           >
              <ArrowLeft size={18} /> Back to Desk
           </button>
           
           <div className="flex items-center gap-4">
              <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-2 ${selectedIssue.status === 'SOLVED' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                 {selectedIssue.status === 'SOLVED' ? <CheckCircle2 size={14}/> : <Clock size={14}/>}
                 {selectedIssue.status}
              </span>
           </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[calc(100vh-180px)] min-h-[600px]">
           
           {/* LEFT: Ticket Context Card */}
           <div className="lg:col-span-1 bg-gray-50 rounded-[30px] p-8 border border-gray-100 flex flex-col h-full">
              <div className="mb-8">
                 <h1 className="text-3xl font-black text-gray-900 mb-2">{selectedIssue.id}</h1>
                 <p className="text-sm font-bold text-[#006d5b] uppercase tracking-wider">{selectedIssue.category}</p>
              </div>

              <div className="space-y-6 flex-1">
                 <div className="bg-white p-5 rounded-2xl border border-gray-200/50 shadow-sm">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                       <User size={12}/> Reported By
                    </p>
                    <div className="flex items-center gap-3">
                       <div className="w-10 h-10 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold">
                          {selectedIssue.raisedBy.charAt(0)}
                       </div>
                       <div>
                          <p className="font-bold text-gray-900 text-sm">{selectedIssue.raisedBy}</p>
                          <p className="text-xs text-gray-500 font-medium">{selectedIssue.raisedByType}</p>
                       </div>
                    </div>
                 </div>

                 <div className="bg-white p-5 rounded-2xl border border-gray-200/50 shadow-sm">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                       <AlertCircle size={12}/> Description
                    </p>
                    <p className="text-gray-800 font-bold text-base leading-relaxed">
                       {selectedIssue.description}
                    </p>
                 </div>
              </div>

              <div className="mt-auto pt-6 border-t border-gray-200">
                 {selectedIssue.status === 'PENDING' && (
                    <button 
                      onClick={() => handleSolve(selectedIssue.id)} 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-2"
                    >
                       <CheckCircle2 size={18}/> Mark as Resolved
                    </button>
                 )}
              </div>
           </div>

           {/* RIGHT: Conversation Console */}
           <div className="lg:col-span-2 bg-white rounded-[30px] border border-gray-200 shadow-xl shadow-gray-100 overflow-hidden flex flex-col">
              
              {/* Chat Header */}
              <div className="p-6 border-b border-gray-100 bg-gray-50/30 flex justify-between items-center">
                 <h3 className="font-black text-gray-900 text-sm uppercase tracking-wide flex items-center gap-2">
                    <MessageSquare size={16} className="text-[#006d5b]"/> Activity Log
                 </h3>
                 <span className="text-xs font-bold text-gray-400">Target: {selectedIssue.target}</span>
              </div>

              {/* Chat Body */}
              <div className="flex-1 overflow-y-auto p-8 space-y-6 bg-white">
                 {selectedIssue.history.map((chat: any, idx: number) => (
                    <div key={idx} className={`flex ${chat.role === 'ADMIN' ? 'justify-end' : 'justify-start'}`}>
                       <div className={`max-w-[80%] p-5 rounded-2xl shadow-sm border text-sm font-medium leading-relaxed ${
                          chat.role === 'ADMIN' 
                             ? 'bg-[#006d5b] text-white border-[#006d5b] rounded-br-none' 
                             : chat.role === 'SYSTEM'
                                ? 'bg-gray-100 text-gray-500 border-transparent w-full text-center text-xs font-bold shadow-none'
                                : 'bg-gray-50 text-gray-800 border-gray-100 rounded-bl-none'
                       }`}>
                          {chat.role !== 'SYSTEM' && (
                             <div className="flex justify-between items-center mb-2 gap-4 opacity-70">
                                <span className="text-[10px] font-black uppercase tracking-wider">{chat.sender}</span>
                                <span className="text-[9px]">{chat.time}</span>
                             </div>
                          )}
                          {chat.message}
                       </div>
                    </div>
                 ))}
              </div>

              {/* Chat Input */}
              <div className="p-6 border-t border-gray-100 bg-gray-50/30">
                 <div className="relative">
                    <textarea 
                      value={adminReply}
                      onChange={(e) => setAdminReply(e.target.value)}
                      placeholder="Type your reply or internal note here..."
                      className="w-full bg-white border border-gray-200 rounded-2xl p-4 pr-32 text-sm font-medium focus:ring-2 focus:ring-[#006d5b]/10 focus:border-[#006d5b] outline-none h-20 resize-none shadow-sm transition-all"
                    />
                    <div className="absolute bottom-3 right-3">
                       <button 
                         onClick={addReply} 
                         disabled={!adminReply.trim()}
                         className="bg-[#006d5b] hover:bg-[#005a4b] text-white px-5 py-2 rounded-xl font-bold text-xs uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-md"
                       >
                          Send <CornerDownLeft size={14}/>
                       </button>
                    </div>
                 </div>
              </div>

           </div>

        </div>
      </div>
    </div>
  );
};

export default IssuesPage;